import { Popconfirm, Space, Table, notification } from "antd";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import React, { useCallback } from "react";
import { useEffect, useState } from "react";

import { deleteUserById } from "../../store/api/users";
import { formatDate } from "../../utils/date";
import UsersToolbar from "./UsersToolbar";
import AddUserPopup from "./AddUserPopup";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";

const UsersTable = ({ users, handleFetch, loading, total, showFilters }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const [filters, setFilters] = useState({
    searchKeyword: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [confirmLoading, setConfirmLoading] = useState(false);

  const authUser = useSelector(selectUser);

  const refetchData = useCallback(() => {
    handleFetch({
      ...filters,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [filters, pagination, handleFetch]);

  useEffect(() => {
    handleFetch({
      ...filters,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [filters, pagination, handleFetch]);

  const handleFiltersChange = (filterKey, filterValue) => {
    setFilters((prev) => ({ ...prev, [filterKey]: filterValue }));
    setPagination({
      current: 1,
      pageSize: pagination.pageSize,
    });
  };

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleDelete = (item) => {
    setConfirmLoading(true);
    deleteUserById(item.id)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: data.message });
        refetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const closePopup = () => {
    setIsModalVisible(false);
    setActiveItem(null);
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Name",
      dataIndex: "fullName",
      width: "250px",
    },
    {
      title: "Added On",
      dataIndex: "dates",
      render: (_, record) => {
        return <p>{formatDate(_.created_at)}</p>;
      },
      width: "150px",
    },
  ];

  if (authUser?.isSuperAdmin) {
    columns.push({
      title: "Actions",
      dataIndex: "actions",
      width: "100px",
      render: (_, record) => {
        return (
          <Space style={{ justifyContent: "center", width: "100%" }}>
            <EditTwoTone
              title="Edit"
              twoToneColor="#1890ff"
              style={{
                fontSize: "20px",
                cursor: "pointer",
              }}
              onClick={() => {
                setActiveItem(record);
                setIsModalVisible(true);
              }}
            />

            {record.id !== authUser.id && (
              <Popconfirm
                confirmLoading={confirmLoading}
                title="Are you sure want to delete this user?"
                onConfirm={() => handleDelete(record)}
                placement="topRight"
              >
                <DeleteTwoTone
                  title="Delete"
                  twoToneColor="#ff0000"
                  style={{
                    color: "gray",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                />
              </Popconfirm>
            )}
          </Space>
        );
      },
    });
  }

  return (
    <>
      {isModalVisible && (
        <AddUserPopup
          closePopup={closePopup}
          item={activeItem}
          authUser={authUser}
          onCreateSuccess={refetchData}
          onUpdateSuccess={refetchData}
        />
      )}
      <UsersToolbar
        showFilters={showFilters}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onAddUser={() => setIsModalVisible(true)}
        showAddUser={authUser.isSuperAdmin}
      />
      <Table
        dataSource={users}
        columns={columns}
        loading={loading}
        bordered
        rowKey="id"
        pagination={{
          pageSizeOptions: ["10", "20", "50"],
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
        }}
        onChange={handleTableChange}
      />
    </>
  );
};

export default UsersTable;
